/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef } from 'react';

import { css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl';

import { jsx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags';
import Tag, { SimpleTag } from '@atlaskit/tag';
import type { AppearanceType } from '@atlaskit/tag';

import messages from './messages';

type Props = {
	appearance?: AppearanceType;
	elemBefore?: React.ReactNode;
	label: string;
	onClick?: () => void;
};

const cssVars = {
	alignItems: '--align-items',
	elemBeforeHeight: '--elem-before-height',
};

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const refinementTagStylesVisualRefresh = cssUnbounded({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> span': {
		margin: 0,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		alignItems: `var(${cssVars.alignItems})`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'> span': {
			maxWidth: 'var(--content-width)',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
			'&:has([data-testid="command-palette-rovo-agent-icon"])': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
				height: `var(${cssVars.elemBeforeHeight})`,
				marginInline: 0,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
				'> div': {
					marginInline: 0,
				},
			},
		},
	},
});

/**
 * RefinementTag is the component which renders the
 * title of preset prompts such as
 * - Brainstorm
 * - Summarize
 *
 * When rendered as part of a user input field, it should provide an
 * onClick to display the remove button to allow users to remove the tag.
 * When rendered as a presentational element to show which
 * preset prompt has been used, it should not provide an onClick
 * to keep the tag presentational.
 */
export const RefinementTag = forwardRef<HTMLDivElement, Props>(
	({ label, onClick, elemBefore, appearance }, ref) => {
		const styles = {
			[cssVars.alignItems]: appearance === 'rounded' ? 'center' : 'flex-start',
			[cssVars.elemBeforeHeight]: appearance === 'rounded' ? '100%' : 'auto',
		} as React.CSSProperties;

		const { formatMessage } = useIntl();

		return (
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				style={styles}
				// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage, @atlaskit/ui-styling-standard/no-imported-style-values
				css={refinementTagStylesVisualRefresh}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className="refinement-tag"
				data-testid="command-palette-refinement-tag"
				role="status"
				aria-atomic="true"
				aria-label={formatMessage(messages.promptSelected, { promptName: label })}
				aria-live="assertive"
				ref={ref}
			>
				{!onClick ? (
					fg('confluence_fronend_labels_categorization_migration') ? (
						<Tag
							testId={'refinement-tag'}
							text={label}
							elemBefore={elemBefore}
							isRemovable={false}
						/>
					) : (
						<SimpleTag
							testId={'refinement-tag'}
							text={label}
							elemBefore={elemBefore}
							appearance={appearance}
						/>
					)
				) : (
					<Tag
						appearance={
							fg('confluence_fronend_labels_categorization_migration') ? undefined : appearance
						}
						elemBefore={elemBefore}
						testId={'refinement-tag'}
						removeButtonLabel={formatMessage(messages.removeLabel)}
						text={label}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						onBeforeRemoveAction={() => {
							onClick();
							return false;
						}}
					/>
				)}
			</div>
		);
	},
);
